import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { FiEdit2, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import AdminLayout from "../layouts/AdminLayout";
import PageTabs from "../components/PageTabs";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import { services } from "../services";
import { District, State } from "@digital-girls-hub/shared-resources/models/db";
import { useAuth } from "../utils/hooks/useAuth";
import { isStateAdmin, isSuperAdmin } from "../utils/roles";

type PlacementPartnerStateSelection = {
  id: bigint;
  name: string;
  cscId: string;
};

type PlacementPartnerDistrictSelection = {
  id: bigint;
  name: string;
  code: string;
  stateId?: bigint | null;
  stateName?: string | null;
};

type PlacementPartnerRecord = {
  id: bigint;
  name: string;
  stateIds?: bigint[];
  districtIds?: bigint[];
  states?: PlacementPartnerStateSelection[];
  districts?: PlacementPartnerDistrictSelection[];
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  verifiedPlacementTarget?: number | null;
  createdAt: string;
  updatedAt: string;
  vleCount: number;
  placingVLECount: number;
  placedLearnerCount: number;
};

type VLEDataRecord = {
  id: bigint;
  name: string;
  email: string;
  cscId: string;
  stateId: bigint;
  placementPartnerId: bigint | null;
  selfPlacedCount: number;
  placedByPPCount: number;
  totalPlacedCount: number;
  createdAt: string;
  updatedAt: string;
  districtId: bigint;
  userOnboardingCount: number;
  userConfirmedOnboardingCount: number;
  userCourseCompletionCount: number;
  stateName: string;
  districtName: string;
  districtCode: string;
  placementPartnerName: string | null;
};

type VLEListResponse = {
  data: VLEDataRecord[];
  count: number;
};

type SearchFilters = {
  vleCscId: string;
  stateId: number | "";
  districtId: number | "";
};

type PlacementPartnerForm = {
  name: string;
  stateIds: number[];
  districtIds: number[];
  contactPerson: string;
  contactEmail: string;
  contactPhone: string;
  verifiedPlacementTarget: string;
};

type PlacementTab = "summary" | "assignments";

const initialPlacementPartnerForm: PlacementPartnerForm = {
  name: "",
  stateIds: [],
  districtIds: [],
  contactPerson: "",
  contactEmail: "",
  contactPhone: "",
  verifiedPlacementTarget: "",
};

export default function PlacementPartnersPage() {
  const { admin, role } = useAuth();
  const superAdmin = isSuperAdmin(role);
  const stateAdmin = isStateAdmin(role);
  const defaultStateId = admin?.stateId ? Number(admin.stateId) : "";
  const defaultStateIds = defaultStateId ? [defaultStateId] : [];

  const [states, setStates] = useState<State[]>([]);
  const [filterDistricts, setFilterDistricts] = useState<District[]>([]);
  const [formDistricts, setFormDistricts] = useState<District[]>([]);
  const [placementPartners, setPlacementPartners] = useState<
    PlacementPartnerRecord[]
  >([]);
  const [vleList, setVleList] = useState<VLEListResponse>({
    data: [],
    count: 0,
  });
  const [searchForm, setSearchForm] = useState<SearchFilters>({
    vleCscId: "",
    stateId: defaultStateId,
    districtId: "",
  });
  const [filters, setFilters] = useState<SearchFilters>({
    vleCscId: "",
    stateId: defaultStateId,
    districtId: "",
  });
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [form, setForm] = useState<PlacementPartnerForm>({
    ...initialPlacementPartnerForm,
    stateIds: defaultStateIds,
  });
  const [pendingAssignments, setPendingAssignments] = useState<
    Record<string, string>
  >({});
  const [bulkPlacementPartnerId, setBulkPlacementPartnerId] = useState("");
  const [selectedBulkVleIds, setSelectedBulkVleIds] = useState<string[]>([]);
  const [isLoadingPlacementPartners, setIsLoadingPlacementPartners] =
    useState(false);
  const [isLoadingVLEs, setIsLoadingVLEs] = useState(false);
  const [isSavingPlacementPartner, setIsSavingPlacementPartner] =
    useState(false);
  const [isSavingBulkAssignment, setIsSavingBulkAssignment] = useState(false);
  const [savingVleId, setSavingVleId] = useState<string | null>(null);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [editingPlacementPartner, setEditingPlacementPartner] =
    useState<PlacementPartnerRecord | null>(null);
  const [activeTab, setActiveTab] = useState<PlacementTab>("summary");
  const [unassignTarget, setUnassignTarget] = useState<VLEDataRecord | null>(
    null,
  );

  const fetchStates = useCallback(async () => {
    try {
      const data = await services.requestSvc.get<State[]>("/states", {
        withCredentials: true,
      });
      if (admin?.stateId) {
        setStates(
          data.filter((state) => Number(state.id) === Number(admin.stateId)),
        );
        return;
      }
      setStates(data || []);
    } catch (error) {
      setStates([]);
    }
  }, [admin?.stateId]);

  const fetchPlacementPartners = useCallback(async () => {
    setIsLoadingPlacementPartners(true);
    try {
      const data = await services.requestSvc.get<PlacementPartnerRecord[]>(
        "/placement-partners",
        {
          withCredentials: true,
        },
      );
      setPlacementPartners(data || []);
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch placement partners.");
    } finally {
      setIsLoadingPlacementPartners(false);
    }
  }, []);

  const fetchVLEs = useCallback(async () => {
    setIsLoadingVLEs(true);
    try {
      const mappedResponse = await services.requestSvc.post<VLEListResponse>(
        "/get-vles-by-params-paginated",
        {
          params: {
            vleCscId: filters.vleCscId || undefined,
            stateId: filters.stateId || undefined,
            districtId: filters.districtId || undefined,
          },
          paginationParams: {
            pageIndex,
            pageSize,
            orderBy: [],
          },
        },
        {
          withCredentials: true,
        },
      );

      setVleList(mappedResponse);
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch VLEs.");
    } finally {
      setIsLoadingVLEs(false);
    }
  }, [filters, pageIndex, pageSize]);

  useEffect(() => {
    void fetchStates();
    void fetchPlacementPartners();
  }, [fetchPlacementPartners, fetchStates]);

  useEffect(() => {
    if (!superAdmin) {
      setVleList({ data: [], count: 0 });
      return;
    }

    void fetchVLEs();
  }, [fetchVLEs, superAdmin]);

  useEffect(() => {
    const availableIds = new Set(vleList.data.map((row) => String(row.id)));
    setSelectedBulkVleIds((current) =>
      current.filter((value) => availableIds.has(value)),
    );
  }, [vleList.data]);

  useEffect(() => {
    if (!searchForm.stateId) {
      setFilterDistricts([]);
      return;
    }

    void services.requestSvc
      .get<District[]>(`/districts-by-state-id?stateId=${searchForm.stateId}`, {
        withCredentials: true,
      })
      .then((records) => setFilterDistricts(records || []))
      .catch(() => setFilterDistricts([]));
  }, [searchForm.stateId]);

  useEffect(() => {
    if (!form.stateIds.length) {
      setFormDistricts([]);
      setForm((current) => ({
        ...current,
        districtIds: [],
      }));
      return;
    }

    void Promise.all(
      form.stateIds.map((stateId) =>
        services.requestSvc.get<District[]>(
          `/districts-by-state-id?stateId=${stateId}`,
          {
            withCredentials: true,
          },
        ),
      ),
    )
      .then((districtGroups) => {
        const uniqueDistricts = [
          ...new Map(
            districtGroups
              .flat()
              .map((district) => [String(district.id), district]),
          ).values(),
        ];
        const allowedDistrictIds = new Set(
          uniqueDistricts.map((district) => Number(district.id)),
        );

        setFormDistricts(uniqueDistricts);
        setForm((current) => ({
          ...current,
          districtIds: current.districtIds.filter((districtId) =>
            allowedDistrictIds.has(districtId),
          ),
        }));
      })
      .catch(() => {
        setFormDistricts([]);
        setForm((current) => ({
          ...current,
          districtIds: [],
        }));
      });
  }, [form.stateIds]);

  useEffect(() => {
    const nextDefaultStateIds = defaultStateId ? [defaultStateId] : [];
    setSearchForm((current) => ({
      ...current,
      stateId: defaultStateId,
    }));
    setFilters((current) => ({
      ...current,
      stateId: defaultStateId,
    }));
    if (!superAdmin) {
      setForm((current) => ({
        ...current,
        stateIds: nextDefaultStateIds,
        districtIds: [],
      }));
    }
  }, [defaultStateId, superAdmin]);

  useEffect(() => {
    if (!superAdmin && activeTab !== "summary") {
      setActiveTab("summary");
    }
  }, [activeTab, superAdmin]);

  const resetPlacementPartnerForm = useCallback(() => {
    setForm({
      ...initialPlacementPartnerForm,
      stateIds: defaultStateIds,
    });
    setEditingPlacementPartner(null);
    setIsPartnerModalOpen(false);
  }, [defaultStateIds]);

  const handleSearch = () => {
    setPageIndex(0);
    setFilters({ ...searchForm });
  };

  const handleReset = () => {
    const resetState = {
      vleCscId: "",
      stateId: defaultStateId,
      districtId: "",
    } as SearchFilters;
    setPageIndex(0);
    setSearchForm(resetState);
    setFilters(resetState);
  };

  const openCreatePlacementPartnerModal = () => {
    resetPlacementPartnerForm();
    setIsPartnerModalOpen(true);
  };

  const openEditPlacementPartnerModal = (partner: PlacementPartnerRecord) => {
    setEditingPlacementPartner(partner);
    setForm({
      name: partner.name,
      stateIds: (partner.stateIds || []).map(Number),
      districtIds: (partner.districtIds || []).map(Number),
      contactPerson: partner.contactPerson || "",
      contactEmail: partner.contactEmail || "",
      contactPhone: partner.contactPhone || "",
      verifiedPlacementTarget: partner.verifiedPlacementTarget
        ? String(partner.verifiedPlacementTarget)
        : "",
    });
    setIsPartnerModalOpen(true);
  };

  const handlePlacementPartnerSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }

    setIsSavingPlacementPartner(true);
    try {
      const payload = {
        name: form.name.trim(),
        stateIds: form.stateIds,
        districtIds: form.districtIds,
        contactPerson: form.contactPerson || undefined,
        contactEmail: form.contactEmail || undefined,
        contactPhone: form.contactPhone || undefined,
        verifiedPlacementTarget: form.verifiedPlacementTarget
          ? Number(form.verifiedPlacementTarget)
          : undefined,
      };

      if (editingPlacementPartner) {
        await services.requestSvc.put(
          `/placement-partners/${String(editingPlacementPartner.id)}`,
          payload,
          {
            withCredentials: true,
          },
        );
        toast.success("Placement Partner updated successfully.");
      } else {
        await services.requestSvc.post("/placement-partners", payload, {
          withCredentials: true,
        });
        toast.success("Placement Partner created successfully.");
      }

      resetPlacementPartnerForm();
      await fetchPlacementPartners();
    } catch (error: any) {
      toast.error(
        error?.message ||
          `Failed to ${
            editingPlacementPartner ? "update" : "create"
          } placement partner.`,
      );
    } finally {
      setIsSavingPlacementPartner(false);
    }
  };

  const handleDeletePlacementPartner = async (partner: PlacementPartnerRecord) => {
    const shouldDelete = window.confirm(
      `Delete ${partner.name}? All VLEs assigned to this placement partner will be unassigned.`,
    );
    if (!shouldDelete) return;

    try {
      await services.requestSvc.delete(`/placement-partners/${String(partner.id)}`, {
        withCredentials: true,
      });
      toast.success("Placement Partner deleted successfully.");
      await fetchPlacementPartners();
      await fetchVLEs();
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete placement partner.");
    }
  };

  const persistAssignment = async (
    row: VLEDataRecord,
    selectedPlacementPartnerId: string,
  ) => {
    const key = String(row.id);
    setSavingVleId(key);
    try {
      await services.requestSvc.put(
        "/placement-partners/vle-assignment",
        {
          vleId: String(row.id),
          placementPartnerId: selectedPlacementPartnerId || null,
        },
        {
          withCredentials: true,
        },
      );

      toast.success(
        selectedPlacementPartnerId ? "VLE assignment updated." : "VLE unassigned.",
      );
      await fetchPlacementPartners();
      await fetchVLEs();
      setPendingAssignments((current) => {
        const nextState = { ...current };
        delete nextState[key];
        return nextState;
      });
      return true;
    } catch (error: any) {
      toast.error(error?.message || "Failed to update VLE assignment.");
      return false;
    } finally {
      setSavingVleId(null);
    }
  };

  const handleAssignmentSave = async (row: VLEDataRecord) => {
    const key = String(row.id);
    const selectedPlacementPartnerId =
      pendingAssignments[key] ??
      (row.placementPartnerId ? String(row.placementPartnerId) : "");

    if (!selectedPlacementPartnerId && row.placementPartnerId) {
      setUnassignTarget(row);
      return;
    }

    await persistAssignment(row, selectedPlacementPartnerId);
  };

  const handleConfirmUnassign = async () => {
    if (!unassignTarget) return;
    const didSave = await persistAssignment(unassignTarget, "");
    if (didSave) {
      setUnassignTarget(null);
    }
  };

  const handleBulkAssignmentSave = async (placementPartnerId: string | null) => {
    if (!selectedBulkVleIds.length) {
      toast.error("Please select at least one VLE.");
      return;
    }

    if (placementPartnerId === "") {
      toast.error("Please select a placement partner.");
      return;
    }

    if (!placementPartnerId && !window.confirm("Unassign the selected VLEs?")) {
      return;
    }

    setIsSavingBulkAssignment(true);
    try {
      await services.requestSvc.put(
        "/placement-partners/vle-assignment/bulk",
        {
          placementPartnerId,
          vleIds: selectedBulkVleIds,
        },
        {
          withCredentials: true,
        },
      );
      toast.success(
        placementPartnerId ? "VLEs assigned successfully." : "VLEs unassigned successfully.",
      );
      setSelectedBulkVleIds([]);
      await fetchPlacementPartners();
      await fetchVLEs();
    } catch (error: any) {
      toast.error(
        error?.message ||
          (placementPartnerId
            ? "Failed to assign VLEs."
            : "Failed to unassign VLEs."),
      );
    } finally {
      setIsSavingBulkAssignment(false);
    }
  };

  const allVisibleUnmappedSelected =
    vleList.data.length > 0 &&
    selectedBulkVleIds.length === vleList.data.length;

  const placementPartnerColumns = [
      {
        name: "Name",
        selector: (row: PlacementPartnerRecord) => row.name,
        width: "18rem",
      },
      {
        name: "States",
        selector: (row: PlacementPartnerRecord) =>
          row.states?.map((state) => state.name).join(", ") || "-",
        width: "16rem",
      },
      {
        name: "Districts",
        selector: (row: PlacementPartnerRecord) =>
          row.districts?.map((district) => district.name).join(", ") || "-",
        width: "18rem",
      },
      {
        name: "VLEs",
        selector: (row: PlacementPartnerRecord) => row.vleCount || 0,
        width: "8rem",
      },
      {
        name: "Learners Placed",
        selector: (row: PlacementPartnerRecord) => row.placedLearnerCount || 0,
        width: "11rem",
      },
      {
        name: "Contact Person",
        selector: (row: PlacementPartnerRecord) => row.contactPerson || "-",
        width: "14rem",
      },
      {
        name: "Contact Email",
        selector: (row: PlacementPartnerRecord) => row.contactEmail || "-",
        width: "18rem",
      },
      {
        name: "Contact Phone",
        selector: (row: PlacementPartnerRecord) => row.contactPhone || "-",
        width: "14rem",
      },
      {
        name: "Verified Placement Target",
        selector: (row: PlacementPartnerRecord) =>
          row.verifiedPlacementTarget || "-",
        width: "14rem",
      },
      ...(superAdmin
        ? [
            {
              name: "Actions",
              width: "9rem",
              cell: (row: PlacementPartnerRecord) => (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="text-blue-700"
                    onClick={() => openEditPlacementPartnerModal(row)}
                    title="Edit placement partner"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="text-red-600"
                    onClick={() => void handleDeletePlacementPartner(row)}
                    title="Delete placement partner"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              ),
            },
          ]
        : []),
    ];

  return (
    <AdminLayout title="Placement Partners">
      <div className="flex flex-col gap-6 max-w-[1600px]">
        {!superAdmin && (
          <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-xl px-4 py-3 text-sm">
            You can view placement partner summary here. Assignment and partner
            management actions are limited to Super Admin.
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm">
          <PageTabs
            tabs={[
              { id: "summary", label: "Partner Summary" },
              ...(superAdmin
                ? [{ id: "assignments" as PlacementTab, label: "Assignments" }]
                : []),
            ]}
            activeTab={activeTab}
            onChange={(tabId) => setActiveTab(tabId as PlacementTab)}
          />
        </div>

        {activeTab === "summary" && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Placement Partner Summary
              </h2>
              {superAdmin && (
                <button
                  type="button"
                  className="btn-primary inline-flex items-center gap-2"
                  onClick={openCreatePlacementPartnerModal}
                >
                  <FiPlus className="h-4 w-4" />
                  Add Partner
                </button>
              )}
            </div>
            <DataTable<PlacementPartnerRecord>
              columns={placementPartnerColumns}
              data={placementPartners}
              progressPending={isLoadingPlacementPartners}
              pagination
              className="w-full"
              noDataComponent="No placement partners found."
            />
          </div>
        )}

        {activeTab === "assignments" && superAdmin && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  VLE Assignment
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Search VLEs, bulk update current assignments, and unassign
                  mappings when needed.
                </p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
                <input
                  type="text"
                  className="input-primary"
                  placeholder="Search by CSC ID"
                  value={searchForm.vleCscId}
                  onChange={(event) =>
                    setSearchForm((current) => ({
                      ...current,
                      vleCscId: event.target.value,
                    }))
                  }
                />
                <select
                  className="input-primary"
                  value={searchForm.stateId}
                  onChange={(event) =>
                    setSearchForm((current) => ({
                      ...current,
                      stateId: event.target.value
                        ? Number(event.target.value)
                        : "",
                      districtId: "",
                    }))
                  }
                  disabled={stateAdmin}
                >
                  {!stateAdmin && <option value="">All States</option>}
                  {states.map((state) => (
                    <option key={String(state.id)} value={Number(state.id)}>
                      {state.name}
                    </option>
                  ))}
                </select>
                <select
                  className="input-primary"
                  value={searchForm.districtId}
                  onChange={(event) =>
                    setSearchForm((current) => ({
                      ...current,
                      districtId: event.target.value
                        ? Number(event.target.value)
                        : "",
                    }))
                  }
                >
                  <option value="">All Districts</option>
                  {filterDistricts.map((district) => (
                    <option
                      key={String(district.id)}
                      value={Number(district.id)}
                    >
                      {district.name}
                    </option>
                  ))}
                </select>
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSearch}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </div>
              </div>

              {superAdmin && (
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div className="flex flex-col gap-3">
                    <div>
                      <h3 className="text-base font-semibold text-gray-800">
                        Bulk Assignment
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Select the filtered VLEs on this page, then assign or
                        unassign them in one action. Individual row updates
                        remain available below.
                      </p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-3">
                      <select
                        className="input-primary md:max-w-sm"
                        value={bulkPlacementPartnerId}
                        onChange={(event) =>
                          setBulkPlacementPartnerId(event.target.value)
                        }
                      >
                        <option value="">Select Placement Partner</option>
                        {placementPartners.map((partner) => (
                          <option
                            key={String(partner.id)}
                            value={String(partner.id)}
                          >
                            {partner.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() =>
                          setSelectedBulkVleIds(
                            allVisibleUnmappedSelected
                              ? []
                              : vleList.data.map((row) => String(row.id)),
                          )
                        }
                        disabled={vleList.data.length === 0}
                      >
                        {allVisibleUnmappedSelected
                          ? "Clear Selection"
                          : "Select All"}
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() =>
                          void handleBulkAssignmentSave(bulkPlacementPartnerId)
                        }
                        disabled={isSavingBulkAssignment || !bulkPlacementPartnerId}
                      >
                        {isSavingBulkAssignment ? "Saving..." : "Assign Selected"}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => void handleBulkAssignmentSave(null)}
                        disabled={isSavingBulkAssignment}
                      >
                        {isSavingBulkAssignment ? "Saving..." : "Unassign Selected"}
                      </button>
                    </div>
                    <div className="text-sm text-gray-500">
                      {selectedBulkVleIds.length} of {vleList.data.length} VLEs
                      selected on this page.
                    </div>
                    <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                      {vleList.data.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          No VLEs found for the selected filters.
                        </div>
                      ) : (
                        vleList.data.map((row) => {
                          const rowId = String(row.id);
                          const checked = selectedBulkVleIds.includes(rowId);

                          return (
                            <label
                              key={rowId}
                              className="flex items-center justify-between gap-4 px-4 py-3 border-b last:border-b-0 cursor-pointer"
                            >
                              <div>
                                <div className="font-medium text-sm text-gray-800">
                                  {row.name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {row.cscId} • {row.stateName} •{" "}
                                  {row.districtName || "District not set"} •{" "}
                                  {row.placementPartnerName || "Unassigned"}
                                </div>
                              </div>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  setSelectedBulkVleIds((current) =>
                                    checked
                                      ? current.filter((value) => value !== rowId)
                                      : [...current, rowId],
                                  )
                                }
                              />
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DataTable<VLEDataRecord>
              columns={[
                {
                  id: "vle-id",
                  name: "CSC ID",
                  selector: (row) => row.cscId,
                  width: "12rem",
                },
                {
                  id: "vle-name",
                  name: "VLE Name",
                  selector: (row) => row.name,
                  width: "16rem",
                },
                {
                  name: "State",
                  selector: (row) => row.stateName,
                  width: "12rem",
                },
                {
                  name: "District",
                  selector: (row) => row.districtName || "-",
                  width: "12rem",
                },
                {
                  name: "Current PP",
                  selector: (row) => row.placementPartnerName || "Unassigned",
                  width: "15rem",
                },
                {
                  name: "Self Placed",
                  selector: (row) => row.selfPlacedCount || 0,
                  width: "10rem",
                },
                {
                  name: "Placed by PP",
                  selector: (row) => row.placedByPPCount || 0,
                  width: "10rem",
                },
                {
                  name: "Total Placed",
                  selector: (row) => row.totalPlacedCount || 0,
                  width: "10rem",
                },
                {
                  name: "Assign / Unassign",
                  width: "24rem",
                  cell: (row) => {
                    const rowKey = String(row.id);
                    const selectedPlacementPartnerId =
                      pendingAssignments[rowKey] ??
                      (row.placementPartnerId
                        ? String(row.placementPartnerId)
                        : "");
                    const hasChanged =
                      selectedPlacementPartnerId !==
                      (row.placementPartnerId
                        ? String(row.placementPartnerId)
                        : "");

                    return (
                      <div className="flex items-center gap-2 w-full">
                        <select
                          className="input-primary !py-2 !px-3"
                          value={selectedPlacementPartnerId}
                          onChange={(event) =>
                            setPendingAssignments((current) => ({
                              ...current,
                              [rowKey]: event.target.value,
                            }))
                          }
                          disabled={!superAdmin}
                        >
                          <option value="">Unassigned</option>
                          {placementPartners.map((partner) => (
                            <option
                              key={String(partner.id)}
                              value={String(partner.id)}
                            >
                              {partner.name}
                            </option>
                          ))}
                        </select>
                        {superAdmin && (
                          <button
                            type="button"
                            className="btn-primary whitespace-nowrap"
                            onClick={() => void handleAssignmentSave(row)}
                            disabled={!hasChanged || savingVleId === rowKey}
                          >
                            {savingVleId === rowKey ? "Saving..." : "Save"}
                          </button>
                        )}
                      </div>
                    );
                  },
                },
              ]}
              data={vleList.data}
              progressPending={isLoadingVLEs}
              pagination
              paginationServer
              paginationPerPage={pageSize}
              paginationTotalRows={vleList.count}
              paginationRowsPerPageOptions={[5, 10, 20, 50]}
              onChangePage={(page) => {
                setPageIndex(page - 1);
              }}
              onChangeRowsPerPage={(currentRowsPerPage) => {
                setPageIndex(0);
                setPageSize(currentRowsPerPage);
              }}
              className="w-full mt-6"
              noDataComponent="No VLEs found for the selected filters."
            />
          </div>
        )}
      </div>

      {isPartnerModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {editingPlacementPartner
                    ? "Edit Placement Partner"
                    : "Create Placement Partner"}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Placement partner names are checked case-insensitively.
                </p>
              </div>
              <button
                type="button"
                className="text-gray-500"
                onClick={resetPlacementPartnerForm}
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <input
                className="input-primary"
                placeholder="Placement Partner Name"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
              />
              <input
                className="input-primary"
                placeholder="Contact Person"
                value={form.contactPerson}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    contactPerson: event.target.value,
                  }))
                }
              />
              <input
                className="input-primary"
                placeholder="Contact Email"
                value={form.contactEmail}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    contactEmail: event.target.value,
                  }))
                }
              />
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                className="input-primary"
                placeholder="Contact Phone"
                value={form.contactPhone}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    contactPhone: event.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10),
                  }))
                }
              />
              <input
                type="number"
                min={1}
                className="input-primary"
                placeholder="Verified Placement Target"
                value={form.verifiedPlacementTarget}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    verifiedPlacementTarget: event.target.value,
                  }))
                }
              />
              <div className="md:col-span-2 xl:col-span-3 grid grid-cols-1 xl:grid-cols-2 gap-4">
                <MultiSelectDropdown
                  label="States"
                  options={states.map((state) => ({
                    label: state.name,
                    value: Number(state.id),
                  }))}
                  selectedValues={form.stateIds}
                  onChange={(stateIds) =>
                    setForm((current) => ({
                      ...current,
                      stateIds,
                    }))
                  }
                  placeholder="Select states"
                  emptyText="No states available."
                />
                <MultiSelectDropdown
                  label="Districts"
                  options={formDistricts.map((district) => ({
                    label: district.name,
                    value: Number(district.id),
                  }))}
                  selectedValues={form.districtIds}
                  onChange={(districtIds) =>
                    setForm((current) => ({
                      ...current,
                      districtIds,
                    }))
                  }
                  placeholder="Select districts"
                  emptyText={
                    form.stateIds.length === 0
                      ? "Select one or more states to choose districts."
                      : "No districts available for the selected states."
                  }
                  disabled={!form.stateIds.length}
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                className="btn-secondary"
                onClick={resetPlacementPartnerForm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void handlePlacementPartnerSubmit()}
                disabled={isSavingPlacementPartner}
              >
                {isSavingPlacementPartner
                  ? "Saving..."
                  : editingPlacementPartner
                  ? "Save Changes"
                  : "Create Partner"}
              </button>
            </div>
          </div>
        </div>
      )}

      {unassignTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Confirm Unassign
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  This will remove the current placement partner mapping for the
                  selected VLE.
                </p>
              </div>
              <button
                type="button"
                className="text-gray-500"
                onClick={() => setUnassignTarget(null)}
                disabled={savingVleId === String(unassignTarget.id)}
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 text-sm text-gray-700">
              <p>
                Unassign <span className="font-semibold">{unassignTarget.name}</span>{" "}
                from{" "}
                <span className="font-semibold">
                  {unassignTarget.placementPartnerName || "this placement partner"}
                </span>
                ?
              </p>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setUnassignTarget(null)}
                disabled={savingVleId === String(unassignTarget.id)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void handleConfirmUnassign()}
                disabled={savingVleId === String(unassignTarget.id)}
              >
                {savingVleId === String(unassignTarget.id)
                  ? "Saving..."
                  : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
